import { Controller } from '@hotwired/stimulus'

// Prompt playground on the conversation debug page. Lazily loads the editor
// panel for an agent message, collects edited message contents, replays the
// thread against the model, and renders the new output beside the original.
export default class extends Controller {
  static targets = [
    'panel',
    'override',
    'runButton',
    'runSpinner',
    'result',
    'newContent',
    'newFunctionCall',
    'alert',
    'alertMessage',
  ]

  static values = {
    newUrl: String,
    runUrl: String,
  }

  async toggle() {
    if (!this.loaded) {
      try {
        const html = await this._fetchText(this.newUrlValue)
        this.panelTarget.innerHTML = html
        this.loaded = true
      } catch (e) {
        const error = document.createElement('p')
        error.className = 'text-sm text-red-600'
        error.textContent = `Falha ao carregar o painel: ${e.message}`
        this.panelTarget.replaceChildren(error)
        this.loaded = true
      }
    }
    this.panelTarget.classList.toggle('hidden')
  }

  async run(event) {
    event.preventDefault()
    this._hideAlert()
    this._setBusy(true)

    const overrides = {}
    this.overrideTargets.forEach((el) => {
      overrides[el.dataset.messageId] = el.value
    })

    try {
      const response = await this._post(this.runUrlValue, overrides)
      this._renderResult(response)
    } catch (e) {
      this._showAlert(`Falha ao rodar o replay: ${e.message}`)
    } finally {
      this._setBusy(false)
    }
  }

  _renderResult(response) {
    const content = (response.content || '').trim()
    const paragraph = document.createElement('p')
    if (content) {
      paragraph.className = 'text-sm text-gray-800 whitespace-pre-wrap'
      paragraph.textContent = content
    } else {
      paragraph.className = 'text-sm text-gray-400 italic'
      paragraph.textContent = '(sem conteúdo de texto)'
    }
    this.newContentTarget.replaceChildren(paragraph)

    if (response.function_call) {
      this.newFunctionCallTarget.textContent = JSON.stringify(this._withParsedArgs(response.function_call), null, 2)
      this.newFunctionCallTarget.classList.remove('hidden')
    } else {
      this.newFunctionCallTarget.textContent = ''
      this.newFunctionCallTarget.classList.add('hidden')
    }

    this.resultTarget.classList.remove('hidden')
    this.resultTarget.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }

  async _fetchText(url) {
    const response = await fetch(url, {
      headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
    })
    if (!response.ok) throw new Error(response.statusText)
    return response.text()
  }

  async _post(url, overrides) {
    const formData = new FormData()
    Object.entries(overrides).forEach(([id, value]) => formData.append(`overrides[${id}]`, value))

    const response = await fetch(url, {
      method: 'POST',
      headers: { 'X-CSRF-Token': this._csrfToken(), Accept: 'application/json' },
      body: formData,
    })

    if (!response.ok) {
      const text = await response.text()
      throw new Error(text || response.statusText)
    }
    return response.json()
  }

  _withParsedArgs(functionCall) {
    if (!functionCall || !functionCall.arguments) return functionCall
    if (typeof functionCall.arguments !== 'string') return functionCall
    try {
      return { ...functionCall, arguments: JSON.parse(functionCall.arguments) }
    } catch (e) {
      return functionCall
    }
  }

  _setBusy(busy) {
    this.runButtonTarget.disabled = busy
    this.runSpinnerTarget.classList.toggle('hidden', !busy)
  }

  _csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ''
  }

  _showAlert(message) {
    this.alertMessageTarget.textContent = message
    this.alertTarget.classList.remove('hidden')
  }

  _hideAlert() {
    this.alertTarget.classList.add('hidden')
  }
}
